import { useCallback, useEffect, useMemo, useState } from "react";
import {
  API_ROUTES,
  EIGHTBALL_GROUP_LABEL,
  EIGHTBALL_SOLID_COLORS,
  EIGHTBALL_STRIPE_COLORS,
  POOL,
  clampPower,
  suitOf,
  type EightballJoinResponse,
  type EightballLeaderboardResponse,
  type EightballRoomsResponse,
  type EightballSnapshot,
  type SuitGroup,
} from "@ziolo/shared";
import { Button, Card } from "@ziolo/ui";
import { Field } from "../components/Field.js";
import { apiRequest, errorMessage } from "../lib/apiClient.js";
import { useSession } from "../session/SessionProvider.js";
import { EightballTable } from "../games/eightball/EightballTable.js";
import { useEightballSocket } from "../games/eightball/useEightballSocket.js";
import "../styles/eightball.css";

function readRoomFromUrl(): string | null {
  const raw = new URLSearchParams(window.location.search).get("room");
  return raw ? raw.toUpperCase() : null;
}

function setRoomInUrl(code: string | null) {
  const url = new URL(window.location.href);
  if (code) url.searchParams.set("room", code);
  else url.searchParams.delete("room");
  window.history.pushState({}, "", url);
}

function miniStyle(id: number): { className: string; style?: { background: string; boxShadow?: string } } {
  if (id === 8) return { className: "eightball-mini eightball-mini--eight" };
  if (id >= 9) {
    const color = EIGHTBALL_STRIPE_COLORS[id] ?? "#888";
    return {
      className: "eightball-mini eightball-mini--stripe",
      style: { background: "#fff", boxShadow: `inset 0 0 0 4px ${color}` },
    };
  }
  return {
    className: "eightball-mini",
    style: { background: EIGHTBALL_SOLID_COLORS[id] ?? "#888" },
  };
}

function PocketedRow({ ids }: { ids: number[] }) {
  if (ids.length === 0) return <span className="eightball-player__meta">—</span>;
  return (
    <div className="eightball-pockets">
      {ids.map((id) => {
        const look = miniStyle(id);
        return (
          <span key={id} className={look.className} style={look.style}>
            {id}
          </span>
        );
      })}
    </div>
  );
}

function groupBalls(state: EightballSnapshot, group: SuitGroup | null): number[] {
  if (!group) return [];
  return state.pocketedIds.filter((id) => suitOf(id) === group);
}

function PlayerCard({
  state,
  seat,
  self,
}: {
  state: EightballSnapshot;
  seat: 0 | 1;
  self: boolean;
}) {
  const player = state.players[seat];
  const group = state.groups[seat];
  const active = !state.gameEnded && state.currentTurn === seat;
  return (
    <div
      className={`eightball-player${seat === 1 ? " eightball-player--right" : ""}${
        active ? " eightball-player--active" : ""
      }`}
    >
      <div className="eightball-player__name">
        {player.name}
        {self ? " (ty)" : ""}
        {player.userId !== null && !player.connected ? " · offline" : ""}
      </div>
      <div className="eightball-player__group">
        {state.openTable ? "otwarty stół" : group ? EIGHTBALL_GROUP_LABEL[group] : "—"}
      </div>
      <PocketedRow ids={groupBalls(state, group)} />
    </div>
  );
}

function Lobby({
  onEnter,
}: {
  onEnter: (code: string) => void;
}) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rooms, setRooms] = useState<EightballRoomsResponse["rooms"]>([]);
  const [board, setBoard] = useState<EightballLeaderboardResponse["leaderboard"]>([]);

  const load = useCallback(() => {
    void apiRequest<EightballRoomsResponse>(API_ROUTES.eightball.rooms)
      .then((data) => setRooms(data.rooms))
      .catch(() => setRooms([]));
    void apiRequest<EightballLeaderboardResponse>(API_ROUTES.eightball.leaderboard)
      .then((data) => setBoard(data.leaderboard))
      .catch(() => setBoard([]));
  }, []);

  useEffect(() => {
    load();
    const id = window.setInterval(load, 4000);
    return () => window.clearInterval(id);
  }, [load]);

  async function go(kind: "quick" | "create" | "join") {
    setBusy(true);
    setError(null);
    try {
      if (kind === "join") {
        const trimmed = code.trim().toUpperCase();
        if (trimmed.length < 3) {
          setError("Podaj kod stołu.");
          return;
        }
        onEnter(trimmed);
        return;
      }
      const path = kind === "quick" ? API_ROUTES.eightball.quick : API_ROUTES.eightball.rooms;
      const data = await apiRequest<EightballJoinResponse>(path, { method: "POST" });
      onEnter(data.code);
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="eightball-lobby" data-testid="eightball-lobby">
      <Card title="Stół" icon="🎱">
        <div className="eightball-form">
          <p className="eightball__hint">
            Dwuosobowy bilard. Zalogowani gracze siadają przy stole, trzecia osoba ogląda.
            Celuj palcem albo kursorem, siłę ustaw suwakiem, kliknij żeby uderzyć.
          </p>
          <div className="eightball-form__row">
            <Button variant="primary" disabled={busy} onClick={() => void go("quick")}>
              Szybka gra
            </Button>
            <Button disabled={busy} onClick={() => void go("create")}>
              Nowy stół
            </Button>
          </div>
          <Field label="Kod stołu" error={error ?? undefined}>
            <input
              value={code}
              maxLength={8}
              onChange={(event) => setCode(event.target.value.toUpperCase())}
              placeholder="np. K7MP2"
            />
          </Field>
          <Button disabled={busy} onClick={() => void go("join")}>
            Dołącz
          </Button>
          <h3 className="z-card__title">Otwarte stoły</h3>
          {rooms.length === 0 ? (
            <p className="hub-hint">Pusto. Załóż stół albo wejdź w szybką grę.</p>
          ) : (
            <ul className="eightball-rooms">
              {rooms.map((room) => (
                <li key={room.code}>
                  <span>
                    <strong>{room.code}</strong> · {room.players[0].name ?? "—"} vs{" "}
                    {room.players[1].name ?? "—"}
                    {room.gameEnded ? " · koniec" : ""}
                  </span>
                  <Button size="sm" onClick={() => onEnter(room.code)}>
                    Wejdź
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>
      <Card title="Ranking" icon="🏆" variant="panel">
        {board.length === 0 ? (
          <p className="hub-hint">Jeszcze nikt nie wygrał partii.</p>
        ) : (
          <ul className="eightball-rank">
            {board.map((row) => (
              <li key={row.username}>
                <span>{row.username}</span>
                <span>
                  {row.wins}W / {row.losses}P
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  );
}

export function EightballPage() {
  const { user, restoring } = useSession();
  const [room, setRoom] = useState<string | null>(readRoomFromUrl);
  const enabled = Boolean(user) && !restoring && Boolean(room);
  const game = useEightballSocket(enabled, room);
  const [power, setPower] = useState(55);
  const [aimAngle, setAimAngle] = useState(0);
  const [ghost, setGhost] = useState({ x: 0, y: 0, active: false });

  useEffect(() => {
    const onPop = () => setRoom(readRoomFromUrl());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    if (!user) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "ArrowUp" || event.key === "w" || event.key === "W") {
        setPower((p) => clampPower(p + 2));
      }
      if (event.key === "ArrowDown" || event.key === "s" || event.key === "S") {
        setPower((p) => clampPower(p - 2));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [user]);

  const enter = useCallback((code: string) => {
    setRoomInUrl(code);
    setRoom(code);
  }, []);

  const leave = useCallback(() => {
    setRoomInUrl(null);
    setRoom(null);
  }, []);

  const turnName = game.state.players[game.state.currentTurn]?.name ?? "—";
  const myTurn = game.mySeat === game.state.currentTurn && (game.mySeat === 0 || game.mySeat === 1);
  const seated = game.mySeat === 0 || game.mySeat === 1;
  const waitingMine =
    game.mySeat === 0
      ? game.state.waitingRematch[0]
      : game.mySeat === 1
        ? game.state.waitingRematch[1]
        : false;
  const pocketedEight = game.state.pocketedIds.includes(8);

  const hint = useMemo(() => {
    if (game.state.gameEnded) return "Partia skończona.";
    if (game.state.ballInHand && myTurn) return "Faul — kliknij stół, żeby postawić białą.";
    if (game.state.simRunning) return "Bile się toczą…";
    if (myTurn) return "Celuj i kliknij stół, żeby uderzyć.";
    if (!seated) return "Oglądasz stół jako widz.";
    return `Tura: ${turnName}`;
  }, [game.state.ballInHand, game.state.gameEnded, game.state.simRunning, myTurn, seated, turnName]);

  if (restoring) return <p className="hub-hint">Ładowanie…</p>;

  if (!user) {
    return (
      <Card title="8ball" icon="🎱">
        <p className="hub-hint">Zaloguj się w belce na górze, żeby usiąść przy stole.</p>
      </Card>
    );
  }

  if (!room) {
    return (
      <div className="eightball">
        <header className="eightball__lobby-head">
          <div>
            <h2 className="eightball__title">
              <span className="z-icon" aria-hidden="true">
                🎱
              </span>
              8ball
            </h2>
            <p className="eightball__welcome">Witaj, {user.username}.</p>
          </div>
        </header>
        <Lobby onEnter={enter} />
      </div>
    );
  }

  return (
    <div className="eightball">
      <header className="eightball__header">
        <div>
          <h2 className="eightball__title">
            <span className="z-icon" aria-hidden="true">
              🎱
            </span>
            8ball · stół {game.state.room || room}
          </h2>
          <p className="eightball__status" data-testid="eightball-turn">
            {game.state.gameEnded
              ? "Koniec partii"
              : `Tura: ${turnName}`}
            {game.connected ? "" : " · łączenie…"}
          </p>
        </div>
        <Button size="sm" onClick={leave}>
          Lobby
        </Button>
      </header>

      {game.error ? <p className="hub-error">{game.error}</p> : null}
      {game.notice ? <p className="eightball__notice">{game.notice}</p> : null}

      <div className="eightball__layout">
        <div className="eightball__stage">
          <div className="eightball-hud">
            <PlayerCard state={game.state} seat={0} self={game.mySeat === 0} />
            <div className="eightball-turn">
              <strong>{hint}</strong>
              {game.state.message ? <p className="eightball__hint">{game.state.message}</p> : null}
              {game.state.openTable && game.state.pocketedIds.length > 0 ? (
                <PocketedRow ids={game.state.pocketedIds} />
              ) : null}
            </div>
            <PlayerCard state={game.state} seat={1} self={game.mySeat === 1} />
          </div>

          <EightballTable
            state={game.state}
            mySeat={game.mySeat}
            power={power}
            aimAngle={aimAngle}
            ghost={ghost}
            onAim={setAimAngle}
            onPlace={(x, y) => game.placeCue(x, y)}
            onStrike={() => game.strike(aimAngle, power)}
            onGhost={(x, y, active) => setGhost({ x, y, active })}
            onPowerDelta={(delta) => setPower((p) => clampPower(p + delta))}
          />

          <div className="eightball-controls">
            <label>
              Siła
              <input
                type="range"
                min={POOL.MIN_POWER}
                max={POOL.MAX_POWER}
                value={power}
                onChange={(event) => setPower(clampPower(Number(event.target.value)))}
              />
            </label>
            {game.state.gameEnded && seated ? (
              <div className="eightball-actions">
                <Button
                  variant="primary"
                  disabled={waitingMine}
                  onClick={game.requestRematch}
                >
                  {waitingMine ? "Czekam…" : "Rewanż"}
                </Button>
                <Button onClick={game.declineRematch}>Nie</Button>
              </div>
            ) : null}
          </div>
        </div>

        <aside className="eightball__side">
          <Card title="Zasady" icon="📘" variant="panel">
            <p className="eightball__hint">
              Po pierwszym legalnym wbiciu dostajesz całe albo połówki. Wbij swoją grupę,
              potem czarną. Faul (biała, pudło, zła pierwsza bila) — przeciwnik stawia białą.
              Czarna za wcześnie albo z faulem przegrywa.
            </p>
            {pocketedEight ? <p className="eightball__hint">Ósemka już wpadła.</p> : null}
            {game.state.spectatorCount > 0 ? (
              <p className="eightball__hint">Widzów: {game.state.spectatorCount}</p>
            ) : null}
          </Card>
        </aside>
      </div>
    </div>
  );
}
